/**
 * Open the bundled sample database: provision the writable per-user copy via
 * IPC, save + connect it as a normal sqlite connection, and land the user in
 * a query tab whose showcase query executes on mount (results grid visible).
 */
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { toast } from '../../../components/ui/toaster';
import type { Connection } from '../../../types/electron';
import {
  SAMPLE_DB_ALIAS,
  SAMPLE_DB_CONNECTION_ID,
  SAMPLE_DB_SHOWCASE_QUERY,
} from '../sampleDatabaseConstants';
import { safeConnectionServer } from '../../../types/safeConnectionDisplay';

export type SampleDatabaseOpenSource = 'empty_state' | 'command_palette';

function buildSampleConnection(filePath: string): Connection {
  return {
    id: SAMPLE_DB_CONNECTION_ID,
    engine: 'sqlite',
    alias: SAMPLE_DB_ALIAS,
    server: filePath,
    filePath,
    port: '0',
    authentication: 'No password',
    readOnly: false,
  };
}

export async function openSampleDatabase(source: SampleDatabaseOpenSource): Promise<boolean> {
  const provision = await window.electronAPI?.sampleDatabase?.provision();
  if (!provision?.success) {
    toast.error({
      title: 'Could not open the sample database',
      description: provision && !provision.success ? provision.error : 'Electron API unavailable',
    });
    return false;
  }

  const connection = buildSampleConnection(provision.filePath);
  const { connections, addConnection, updateConnection, connectToDatabase } =
    useConnectionStore.getState();
  const existing = connections.find((c) => c.id === SAMPLE_DB_CONNECTION_ID);
  if (!existing) {
    await addConnection(connection);
  } else if (existing.filePath !== connection.filePath) {
    // userData moved (or the saved record predates a rename) — refresh the path.
    await updateConnection(connection);
  }

  const connected = await connectToDatabase(connection);
  if (!connected) {
    toast.error({
      title: 'Could not open the sample database',
      description: 'Connecting to the local sample file failed.',
    });
    return false;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  addTab({
    title: `Query-${getNextQueryNumber()}`,
    type: 'query',
    connectionInfo: {
      connectionId: SAMPLE_DB_CONNECTION_ID,
      connectionName: SAMPLE_DB_ALIAS,
      serverHost: safeConnectionServer(connection),
    },
    content: {
      query: SAMPLE_DB_SHOWCASE_QUERY,
      autoExecuteOnMount: true,
    },
  });

  // Success side-effect analytics (fire after the flow worked, not on click).
  window.electronAPI?.analytics?.track('feature:sample_db_open', { source })?.catch(() => {});
  return true;
}
